import React, { SyntheticEvent } from 'react';
import { FaEdit, FaPlusCircle, FaTrash } from 'react-icons/fa';

import Checkbox from 'components/common/Checkbox';
import EnumDropdown from 'components/common/EnumDropdown';
import Price from 'components/common/Price';
import PriceInput from 'components/common/PriceInput';
import Fee from 'models/FeeModel';
import FeeType from 'models/types/FeeType';

import axios from 'helpers/axios';
import config from 'helpers/config';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import Percentage from 'components/common/Percentage';
import { PlusCircle } from 'lucide-react';

interface Props {}

interface State {
    fees: Fee[];
    isCreateNewFeeActive: boolean;
    editableFeeId: string | null;
    name: string | null;
    amount: number | null;
    type: FeeType | null;
    isTaxable: boolean;
    isAutoApply: boolean;

    calculateOnLabor: boolean;
    calculateOnParts: boolean;
    calculateOnSublet: boolean;
}

class FeesSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            fees: [],
            isCreateNewFeeActive: false,
            editableFeeId: null,
            amount: null,
            name: null,
            type: null,
            isTaxable: false,
            isAutoApply: false,

            calculateOnLabor: false,
            calculateOnParts: true,
            calculateOnSublet: false,
        };

        this.handleChange = this.handleChange.bind(this);
        this.createNewFee = this.createNewFee.bind(this);
        this.updateFee = this.updateFee.bind(this);
    }

    componentDidMount() {
        this.getFees();
    }

    getFees() {
        axios.get(`${config.apiUrl}/fee`).then((res) => this.setState({ fees: res.data }));
    }

    createNewFee() {
        const { name, amount, type, isTaxable, isAutoApply, calculateOnLabor, calculateOnParts, calculateOnSublet } =
            this.state;
        const fee = {
            name,
            amount: Number(amount),
            type,
            isTaxable,
            isAutoApply,
            // "applies to" only matters for percentage fees; fixed fees ignore it.
            calculateOnLabor: type === FeeType.Percentage ? calculateOnLabor : false,
            calculateOnParts: type === FeeType.Percentage ? calculateOnParts : false,
            calculateOnSublet: type === FeeType.Percentage ? calculateOnSublet : false,
        };

        axios.post(`${config.apiUrl}/fee`, fee).then((_) => {
            this.getFees();
            this.setState({ name: null, amount: null, type: null, isTaxable: true, isCreateNewFeeActive: false });
        });
    }

    updateFee() {
        const {
            name,
            amount,
            type,
            isTaxable,
            isAutoApply,
            editableFeeId,
            calculateOnLabor,
            calculateOnParts,
            calculateOnSublet,
        } = this.state;
        const fee = {
            name,
            type,
            amount: Number(amount?.toString().replace(/,/g, '')),
            isTaxable,
            isAutoApply,
            id: editableFeeId,
            // "applies to" only matters for percentage fees; fixed fees ignore it.
            calculateOnLabor: type === FeeType.Percentage ? calculateOnLabor : false,
            calculateOnParts: type === FeeType.Percentage ? calculateOnParts : false,
            calculateOnSublet: type === FeeType.Percentage ? calculateOnSublet : false,
        };

        axios.put(`${config.apiUrl}/fee`, fee).then((_) => {
            this.setState(
                {
                    name: null,
                    amount: null,
                    type: null,
                    isTaxable: true,
                    isCreateNewFeeActive: false,
                    editableFeeId: null,
                    calculateOnLabor: false,
                    calculateOnParts: true,
                    calculateOnSublet: false,
                },
                this.getFees,
            );
        });
    }

    handleDelete(fee: Fee) {
        displayConfirmationModal(`fee: "${fee.name}"`)
            .then(() => {
                axios.delete(`${config.apiUrl}/fee/${fee.id}`).then(() => {
                    this.getFees();
                    this.setState({
                        name: null,
                        amount: null,
                        type: null,
                        isTaxable: true,
                        isCreateNewFeeActive: false,
                        calculateOnLabor: false,
                        calculateOnParts: true,
                        calculateOnSublet: false,
                    });
                });
            })
            .catch((_) => _);
    }

    toggleFeeActive(fee: Fee) {
        axios.put(`${config.apiUrl}/fee`, { id: fee.id, isActive: !fee.isActive }).then(() => this.getFees());
    }

    editFee(fee: Fee) {
        this.setState({
            editableFeeId: fee.id,
            name: fee.name,
            amount: fee.amount,
            type: fee.type,
            isCreateNewFeeActive: false,
            isTaxable: fee.isTaxable,
            isAutoApply: fee.isAutoApply,
            calculateOnLabor: fee.calculateOnLabor,
            calculateOnParts: fee.calculateOnParts,
            calculateOnSublet: fee.calculateOnSublet,
        });
    }
    handleChange(e: SyntheticEvent) {
        // @ts-ignore
        const { name, value } = e.currentTarget;
        // @ts-ignore
        this.setState({ [name]: value });
    }

    renderFeeCard(fee: Fee) {
        const { editableFeeId } = this.state;
        const isEditing = fee.id === editableFeeId;

        if (isEditing) {
            return this.renderEditableFeeCard(true, fee.id);
        }

        const appliesTo = [];
        if (fee.calculateOnLabor) appliesTo.push('Labor');
        if (fee.calculateOnParts) appliesTo.push('Parts');
        if (fee.calculateOnSublet) appliesTo.push('Sublets');

        return (
            <div
                key={fee.id}
                className={`bg-white rounded-lg border border-gray-200 p-6 mb-4 ${fee.isActive ? '' : 'opacity-60'}`}
            >
                <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center">
                        <div
                            className={`w-3 h-3 rounded-full mr-3 ${fee.isActive ? 'bg-green-500' : 'bg-gray-400'}`}
                        ></div>
                        <h3 className="text-lg font-semibold text-gray-900">{fee.name}</h3>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => this.toggleFeeActive(fee)}
                            title={fee.isActive ? 'Click to deactivate' : 'Click to activate'}
                            className={`px-2 py-1 rounded-md text-sm font-medium clickable ${
                                fee.isActive
                                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {fee.isActive ? 'Active' : 'Inactive'}
                        </button>
                        <button onClick={() => this.editFee(fee)} className="text-gray-400 hover:text-blue-500">
                            <FaEdit className="w-4 h-4" />
                        </button>
                        <button onClick={() => this.handleDelete(fee)} className="text-gray-400 hover:text-red-500">
                            <FaTrash className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <span className="text-sm font-medium text-gray-600">
                            {fee.type === FeeType.Percentage ? '% Percentage' : '$ Fixed Amount'}
                        </span>
                        {fee.isTaxable && (
                            <span className="ml-2 bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">Taxable</span>
                        )}
                    </div>
                </div>

                <div className="mb-4">
                    <div className="text-sm text-gray-600 mb-2">Amount</div>
                    <div className="text-2xl font-bold text-gray-900">
                        {fee.type === FeeType.Percentage ? `${fee.amount}%` : `$${fee.amount?.toFixed(2)}`}
                    </div>
                </div>

                <div className="mb-4">
                    <div className="text-sm text-gray-600 mb-2">Applies To</div>
                    <div className="flex space-x-2">
                        {appliesTo.map((item) => (
                            <span key={item} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                                {item}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="text-sm text-gray-500">
                    Preview (on $100 job):{' '}
                    <span className="text-green-600 font-semibold">
                        ${(fee.type === FeeType.Percentage ? (fee.amount / 100) * 100 : fee.amount)?.toFixed(2)}
                    </span>
                </div>
            </div>
        );
    }

    renderEditableFeeCard(isEdit: boolean, feeId?: string) {
        return (
            <div key={feeId || 'new'} className="bg-white rounded-lg border border-blue-300 p-6 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fee Name</label>
                        <input
                            name="name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onChange={this.handleChange}
                            value={this.state.name || ''}
                            placeholder="Enter fee name"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fee Type</label>
                        <EnumDropdown
                            items={FEE_TYPES}
                            bindName={'name'}
                            onSelect={(feeType: any) => {
                                this.handleChange({
                                    currentTarget: {
                                        name: 'type',
                                        value: feeType.type,
                                    },
                                } as React.FormEvent<any>);
                            }}
                            selected={this.state.type}
                            id={feeId || 'newFee'}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                        <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500 font-medium">
                                {this.state.type === FeeType.Fixed ? '$' : '%'}
                            </span>
                            <PriceInput
                                name="amount"
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                onChange={this.handleChange}
                                amount={this.state.amount || 0}
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                        <div className="flex items-center">
                            <Checkbox
                                name="isTaxable"
                                value={this.state.isTaxable}
                                onChange={this.handleChange}
                                size={4}
                            />
                            <span className="ml-2 text-sm text-gray-700">Taxable</span>
                        </div>
                        <div className="flex items-center mt-2">
                            <Checkbox
                                name="isAutoApply"
                                value={this.state.isAutoApply}
                                onChange={this.handleChange}
                                size={4}
                            />
                            <span className="ml-2 text-sm text-gray-700">
                                Auto-apply to new services
                            </span>
                        </div>
                    </div>
                </div>

                {this.state.type === FeeType.Percentage && (
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Calculate On</label>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-center">
                            <Checkbox
                                name="calculateOnLabor"
                                value={this.state.calculateOnLabor}
                                onChange={this.handleChange}
                                size={4}
                            />
                            <span className="ml-2 text-sm text-gray-700">Labor</span>
                        </div>
                        <div className="flex items-center">
                            <Checkbox
                                name="calculateOnParts"
                                value={this.state.calculateOnParts}
                                onChange={this.handleChange}
                                size={4}
                            />
                            <span className="ml-2 text-sm text-gray-700">Parts</span>
                        </div>
                        <div className="flex items-center">
                            <Checkbox
                                name="calculateOnSublet"
                                value={this.state.calculateOnSublet}
                                onChange={this.handleChange}
                                size={4}
                            />
                            <span className="ml-2 text-sm text-gray-700">Sublets</span>
                        </div>
                    </div>
                </div>
                )}

                <div className="flex space-x-3">
                    <button
                        onClick={isEdit ? this.updateFee : this.createNewFee}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium transition-colors"
                    >
                        Save
                    </button>
                    <button
                        onClick={() =>
                            this.setState({
                                editableFeeId: null,
                                isCreateNewFeeActive: false,
                                name: null,
                                amount: null,
                                type: null,
                                isTaxable: false,
                                calculateOnLabor: false,
                                calculateOnParts: true,
                                calculateOnSublet: false,
                            })
                        }
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md font-medium transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    renderFeeCalculator() {
        const { fees } = this.state;
        const jobAmount = 100; // Default preview amount
        let totalFees = 0;

        return (
            <div className="bg-blue-50 rounded-lg p-6 mt-6 border-blue-700 border">
                <div className="flex items-center mb-4">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1zm1-4a1 1 0 100 2h.01a1 1 0 100-2H7zm2 1a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm4-4a1 1 0 100 2h.01a1 1 0 100-2H13z"
                            clipRule="evenodd"
                        ></path>
                    </svg>
                    <h3 className="text-lg font-semibold text-blue-900">Fee Calculator</h3>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-blue-700 mb-2">Job Amount</label>
                    <input
                        type="number"
                        className="w-32 px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        defaultValue={100}
                        onChange={(e) => {
                            const amount = parseFloat(e.target.value) || 0;
                            // Calculate fees based on amount
                        }}
                    />
                </div>

                {fees.map((fee) => {
                    const feeAmount = fee.type === FeeType.Percentage ? (fee.amount / 100) * jobAmount : fee.amount;
                    totalFees += feeAmount;

                    return (
                        <div key={fee.id} className="flex justify-between py-2 text-sm">
                            <span className="text-blue-700">{fee.name}</span>
                            <span className="text-blue-900 font-medium">${feeAmount?.toFixed(2)}</span>
                        </div>
                    );
                })}

                <div className="border-t border-blue-200 pt-2 mt-2">
                    <div className="flex justify-between font-semibold text-blue-900">
                        <span>Total Fees</span>
                        <span className="text-green-600">${totalFees.toFixed(2)}</span>
                    </div>
                </div>
            </div>
        );
    }

    renderHowFeesWork() {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
                <div className="flex items-center mb-4">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                        ></path>
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">How Shop Fees Work</h3>
                </div>

                <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></span>
                        <span>
                            <strong>Percentage fees</strong> are calculated based on the total job amount
                        </span>
                    </div>
                    <div className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></span>
                        <span>
                            <strong>Fixed fees</strong> are applied as a flat rate regardless of job size
                        </span>
                    </div>
                    <div className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></span>
                        <span>
                            <strong>Fees can be applied</strong> to labor, parts, sublets, or any combination
                        </span>
                    </div>
                    <div className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2"></span>
                        <span>
                            <strong>Taxable fees</strong> will be included in tax calculations
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { fees, isCreateNewFeeActive, editableFeeId } = this.state;

        return (
            // <div className="max-w-4xl mx-auto p-6">
            <div className="flex flex-col  bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Shop Fees</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Shop Fees</h2>
                            <p className="text-gray-600 mt-1">Configure additional fees applied to repair orders</p>
                        </div>

                        {isCreateNewFeeActive || editableFeeId ? null : (
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() =>
                                        this.setState({
                                            isCreateNewFeeActive: !isCreateNewFeeActive,
                                            editableFeeId: null,
                                            name: '',
                                            amount: 0.0,
                                            type: FeeType.Fixed,
                                            isTaxable: true,
                                            calculateOnLabor: false,
                                            calculateOnParts: true,
                                            calculateOnSublet: false,
                                        })
                                    }
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                >
                                    <PlusCircle className="h-4 w-4" />
                                    <span>Add New Fee</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="max-w-4xl mx-auto p-8">
                    {/* Active Fees Section */}
                    <div className="mb-8 ">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Fees ({fees.length})</h2>

                        {/* New Fee Form */}
                        {isCreateNewFeeActive && this.renderEditableFeeCard(false)}

                        {/* Fee Cards */}
                        {fees.map((fee) => this.renderFeeCard(fee))}

                        {/* Empty State */}
                        {fees.length === 0 && !isCreateNewFeeActive && (
                            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                <svg
                                    className="w-12 h-12 text-gray-400 mx-auto mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No fees configured</h3>
                                <p className="text-gray-500 mb-4">You do not have any fees setup yet</p>
                                <button
                                    onClick={() =>
                                        this.setState({
                                            isCreateNewFeeActive: true,
                                            name: '',
                                            amount: 0.0,
                                            type: FeeType.Fixed,
                                            isTaxable: true,
                                            calculateOnLabor: false,
                                            calculateOnParts: true,
                                            calculateOnSublet: false,
                                        })
                                    }
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-colors"
                                >
                                    Add Your First Fee
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Fee Calculator and Info */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {this.renderFeeCalculator()}
                        {this.renderHowFeesWork()}
                    </div>
                </div>
            </div>
        );
    }
}

export default FeesSettings;

const FEE_TYPES = [
    {
        name: 'Percentage %',
        type: FeeType.Percentage,
    },
    {
        name: 'Fixed $',
        type: FeeType.Fixed,
    },
];
